/**
 * B9 · Community template gallery: published plan templates any visitor can
 * browse; coaches can duplicate one straight into their own library.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { Button, Card, EmptyState, ErrorText, Spinner, Tag } from '../../lib/ui.tsx';
import { exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';

interface GalleryTemplate {
  id: string;
  name: string;
  description: string;
  coachName?: string;
  items: { id: string; exerciseId: string; frequencyPerWeek: number }[];
  exercises: { id: string; name: string; tags: string; level: string }[];
}

export default function TemplateGalleryPage() {
  const { me } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [duplicatedId, setDuplicatedId] = useState<string | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['gallery'],
    queryFn: () => api<GalleryTemplate[]>('/api/gallery'),
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => api(`/api/templates/${id}/duplicate`, { method: 'POST', body: {} }),
    onSuccess: (_data, id) => setDuplicatedId(id),
    onError: setError,
  });

  const isCoach = me?.role === 'coach';

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Template gallery</h1>
        <p className="mt-1 text-sm text-slate-500">
          Weekly practice plans shared by coaches on Solfo — built entirely from catalog exercises.
          {!isCoach && ' Coaches can import these into their own library.'}
        </p>
      </header>

      {isLoading ? (
        <Spinner />
      ) : !templates || templates.length === 0 ? (
        <EmptyState title="No published templates yet." hint="Check back soon — coaches publish new plans regularly." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {templates.map((tpl) => (
            <Card key={tpl.id} className="flex h-full flex-col">
              <div className="flex-1">
                <h2 className="text-base font-semibold">{tpl.name}</h2>
                <p className="mt-0.5 text-xs text-slate-400">by {tpl.coachName ?? 'a Solfo coach'}</p>
                {tpl.description && <p className="mt-2 text-sm text-slate-600">{tpl.description}</p>}
                <ul className="mt-3 space-y-1.5">
                  {tpl.exercises.map((ex) => (
                    <li key={ex.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="text-brand-600">♪</span>
                      <span className="min-w-0 truncate">{exLabel(ex)}</span>
                      <Tag>{ex.level}</Tag>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-4">
                {isCoach ? (
                  duplicatedId === tpl.id ? (
                    <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                      ✓ Added to your library — find it under Templates.
                    </p>
                  ) : (
                    <Button
                      variant="secondary"
                      disabled={duplicate.isPending}
                      onClick={() => duplicate.mutate(tpl.id)}
                    >
                      Duplicate into my library
                    </Button>
                  )
                ) : (
                  <p className="text-xs text-slate-400">Coaches can import these.</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <ErrorText error={error} />
    </div>
  );
}
